import { Plus, X } from "lucide-react";

export const LIMITS = { headline: 30, description: 90, path: 15, maxHeadlines: 15, maxDescriptions: 4 };

export const CharInput = ({ value, limit, onChange, onRemove, testid, multiline }) => {
  const len = (value || "").length;
  const over = len > limit;
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="flex items-start gap-2">
      <div className="relative flex-1">
        <Tag data-testid={testid} rows={multiline ? 2 : undefined} value={value} onChange={(e) => onChange(e.target.value)}
          className={`cc-input pr-16 ${multiline ? "resize-y" : ""} ${over ? "!border-red-500/70" : ""}`} />
        <span data-testid={`${testid}-count`} className={`pointer-events-none absolute right-2 top-2 rounded px-1.5 py-0.5 font-mono text-[10px] font-bold ${over ? "bg-red-500/15 text-red-300" : "bg-emerald-500/10 text-emerald-300"}`}>
          {len}/{limit}
        </span>
      </div>
      {onRemove && <button type="button" data-testid={`${testid}-remove`} onClick={onRemove} className="cc-btn cc-btn-ghost mt-0.5 px-2"><X className="h-3.5 w-3.5" /></button>}
    </div>
  );
};

const AdPreview = ({ ad, domain }) => (
  <div className="rounded-md border border-[#1e293b] bg-[#0a101d] p-4" data-testid="rsa-preview">
    <div className="text-xs text-slate-400"><span className="font-semibold text-slate-200">Sponsored</span> · {domain}/{[ad.path1, ad.path2].filter(Boolean).join("/")}</div>
    <div className="mt-1 text-lg leading-snug text-[#8ab4f8]">{(ad.headlines || []).slice(0, 3).join(" | ")}</div>
    <div className="mt-1 text-sm text-slate-400">{(ad.descriptions || []).slice(0, 2).join(" ")}</div>
  </div>
);

export default function RsaEditor({ ad, onChange, testid, domain }) {
  const setArr = (field, arr) => onChange({ ...ad, [field]: arr });
  const block = (field, kind, max) => {
    const arr = ad[field] || [];
    const bad = arr.filter((t) => t.length > LIMITS[kind]).length;
    return (
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="cc-eyebrow">{field} · {arr.length}/{max} · ≤{LIMITS[kind]} chars</div>
          <span data-testid={`${testid}-${field}-status`} className={`font-mono text-[10px] ${bad ? "text-red-300" : "text-emerald-300"}`}>{bad ? `${bad} over limit` : "all within limit"}</span>
        </div>
        <div className="space-y-2">
          {arr.map((t, i) => (
            <CharInput key={i} testid={`${testid}-${kind}-${i}`} value={t} limit={LIMITS[kind]} multiline={kind === "description"}
              onChange={(v) => setArr(field, arr.map((x, j) => (j === i ? v : x)))} onRemove={() => setArr(field, arr.filter((_, j) => j !== i))} />
          ))}
        </div>
        {arr.length < max && (
          <button type="button" data-testid={`${testid}-add-${kind}`} onClick={() => setArr(field, [...arr, ""])} className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300"><Plus className="h-3.5 w-3.5" /> Add {kind}</button>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-5" data-testid={testid}>
      <AdPreview ad={ad} domain={domain} />
      <div className="grid gap-6 xl:grid-cols-2">
        {block("headlines", "headline", LIMITS.maxHeadlines)}
        <div className="space-y-5">
          {block("descriptions", "description", LIMITS.maxDescriptions)}
          <div className="grid grid-cols-2 gap-3">
            {["path1", "path2"].map((p) => (
              <div key={p}><div className="cc-eyebrow mb-2">{p}</div><CharInput testid={`${testid}-${p}`} value={ad[p] || ""} limit={LIMITS.path} onChange={(v) => onChange({ ...ad, [p]: v })} /></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
